import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { api } from "../lib/api";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLine } from "../components/Reveal";

export default function ServiceAreas() {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/areas").then((r) => setAreas(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <Seo
        title="Service Areas | St. Louis, Chesterfield & Surrounding MO Communities | Glow Green"
        description="Glow Green serves St. Louis, Chesterfield, and surrounding Missouri communities with commercial and residential cleaning. Check coverage for your ZIP code."
      />

      <section className="bg-forest grain relative pt-32 lg:pt-44 pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative z-10">
          <p className="uppercase text-xs font-semibold tracking-[0.25em] text-leaf font-mono">Service Areas</p>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
            <MaskedLine delay={0.2}>Local. Nearby.</MaskedLine>
            <MaskedLine delay={0.35} className="text-leaf">Ready to help.</MaskedLine>
          </h1>
          <p className="mt-6 max-w-xl text-white/70 text-base sm:text-lg leading-relaxed">
            Glow Green provides commercial and residential cleaning throughout the greater St. Louis area. If you're close to our listed areas, just ask - we'll confirm coverage for your address.
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          {loading ? (
            <p className="text-slate-500" data-testid="areas-loading">Loading service areas...</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6" data-testid="areas-grid">
              {areas.map((area, i) => (
                <Reveal key={area.id} delay={i * 0.07}>
                  <div className="rounded-2xl bg-white border border-forest/10 p-6 lg:p-8 h-full hover:border-leaf/40 hover:shadow-lg transition-all">
                    <span className="w-10 h-10 rounded-full bg-mint flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-leaf-hover" />
                    </span>
                    <h2 className="mt-4 font-display text-xl font-bold text-forest">{area.name}</h2>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{area.blurb}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          )}

          <Reveal delay={0.15}>
            <div className="mt-14 rounded-2xl bg-forest grain relative overflow-hidden p-8 lg:p-12">
              <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div>
                  <h2 className="font-display text-2xl font-bold text-white">Not sure if we cover your neighborhood?</h2>
                  <p className="mt-2 text-white/70 text-sm max-w-lg">
                    Enter your ZIP code in the quote form - we'll confirm coverage and build your estimate in one step.
                  </p>
                </div>
                <Link to="/contact" data-testid="areas-quote-cta" className="inline-flex items-center gap-2 rounded-full bg-leaf px-7 py-4 font-display font-bold text-sm text-forest-deep transition-all hover:bg-mint hover:scale-[1.03] flex-shrink-0">
                  Check My ZIP Code <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
